import { Component, OnDestroy, OnInit, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { first } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { AuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { ToastService } from '@services/common/toast.service';
import { revealFormErrors } from 'app/main/helpers/form-validation';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    // Public
    public resetPasswordForm!: UntypedFormGroup;
    public resetUsername: string = '';
    public resetCode: string = '';
    public loading = false;

    // Private
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    /**
     * Constructor
     *
     * @param _route
     * @param _router
     * @param _authenticationService
     * @param _formBuilder
     */
    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _authenticationService: AuthenticationService,
        private _formBuilder: UntypedFormBuilder,
        private _translateService: CustomTranslateService,
        private toastService: ToastService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        // redirect to home if already logged in
        if (this._authenticationService.currentUserValue) {
            this._router.navigate(['/']);
        }
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.resetPasswordForm.controls;
    }

    /**
     * On Submit
     */
    onSubmit(): void {
        // stop here if form is invalid, showing what is missing
        if (!revealFormErrors(this.resetPasswordForm, this.host.nativeElement)) {
            return;
        }
        this.loading = true;

        // Reset password
        this._authenticationService
            .resetPassword(this.resetUsername, String(this.f['code'].value), this.f['newPassword'].value as string)
            .pipe(first())
            .subscribe({
                next: () => {
                    this.toastService.showSuccess({ details: 'authentication.resetPassword.message.success' });
                    this._router.navigate(['/auth/login'], { queryParams: { msg: 'Your password has been changed' } });
                    this.loading = false;
                },
                error: (error: Error) => {
                    this.toastService.showError({ details: error.message });
                    this.loading = false;
                },
            });
    }

    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        //TODO: add password match

        // get return url from route parameters or default to '/'
        this.resetUsername = (this._route.snapshot.queryParams['username'] as string) || '';
        if (this.resetUsername === '') {
            this._router.navigate(['/']);
        }
        this.resetCode = (this._route.snapshot.queryParams['passwordResetCode'] as string) || '';

        this.resetPasswordForm = this._formBuilder.group(
            {
                email: [{ value: this.resetUsername, disabled: true }],
                code: [
                    this.resetCode,
                    [
                        Validators.required,
                        Validators.minLength(6),
                        Validators.maxLength(6),
                        Validators.pattern(/[0-9]+/),
                    ],
                ],
                newPassword: ['', [Validators.required, CustomValidators.passwordValidator]],
                confirmPassword: ['', [Validators.required]],
            },
            {
                validators: [CustomValidators.mustMatch('newPassword', 'confirmPassword')],
            },
        );
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
